// The month's folds, and what they mean for a person: the pure core of the fold limit.
//
// John, 2026-10-29: free is FREE_FOLD_CAP folds a calendar month. Past them netti stops folding, and the memory is
// read-only until the month turns or the person moves to plus (unlimited). Reads never stop: every ai a person has
// connected keeps reading everything they made. A month is the calendar month in UTC, the same month the budget
// governor (lib/netti/budget) and fold_month_counts (173) count in.
//
// Who is counted, and from when, is one switch, FOLD_LIMIT (read by lib/billing/foldGate):
//   on       everyone on the free plan
//   testers  the test accounts only (lib/testers/accounts), so the whole path can be lived on production first
//   (unset)  nobody: the meter still reads, nothing stops and no letter goes
//
// NO Supabase, NO env, NO React: the gate, the plan tab, the dashboard band, the letters and the tests import it.

import { FREE_FOLD_CAP, INTRO_TRIGGER_FOLDS } from './plans'

/** settings → plan, where every letter and refusal points */
export const PLAN_PATH = '/dashboard?panel=settings&view=plan'

export type FoldState =
  /** plus, a grant, a founder: nothing is counted against them */
  | 'unlimited'
  /** free, with room */
  | 'open'
  /** free, past the introductory letter's mark and short of the ceiling */
  | 'near'
  /** free, at the ceiling: netti has stopped folding and the memory only reads until resetsAt */
  | 'reached'

export interface FoldStanding {
  /** the calendar month this counts, YYYY-MM (UTC) */
  month: string
  /** folds this month */
  used: number
  /** the ceiling; null when nothing is counted against them */
  cap: number | null
  /** folds left this month; null when unlimited */
  left: number | null
  state: FoldState
  /** when the month turns and the folds come back (UTC midnight, the first of next month) */
  resetsAt: string
  /** whether the limit is held for this person at all (the FOLD_LIMIT switch) */
  enforced: boolean
}

export type FoldLimitMode = 'on' | 'testers' | 'off'

export function foldLimitMode(raw: string | null | undefined): FoldLimitMode {
  const v = (raw ?? '').trim().toLowerCase()
  return v === 'on' ? 'on' : v === 'testers' ? 'testers' : 'off'
}

/** "2026-10" */
export function monthOf(now: number = Date.now()): string {
  const d = new Date(now)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

/** the first instant of this month, UTC */
export function monthStart(now: number = Date.now()): string {
  const d = new Date(now)
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString()
}

/** the first instant of next month, UTC: when the folds come back */
export function nextMonthStart(now: number = Date.now()): string {
  const d = new Date(now)
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString()
}

/** where a person stands this month. `unlimited` = plus, a grant, a founder. */
export function foldStanding(input: { used: number; unlimited: boolean; enforced: boolean; now?: number }): FoldStanding {
  const now = input.now ?? Date.now()
  const used = Math.max(0, Math.floor(input.used))
  const base = { month: monthOf(now), used, resetsAt: nextMonthStart(now), enforced: input.enforced }
  if (input.unlimited) return { ...base, cap: null, left: null, state: 'unlimited' }
  const cap = FREE_FOLD_CAP
  const left = Math.max(0, cap - used)
  const state: FoldState = used >= cap ? 'reached' : used >= INTRO_TRIGGER_FOLDS ? 'near' : 'open'
  return { ...base, cap, left, state }
}

/** true when this person's writes and folds stop now */
export const isHeld = (s: FoldStanding): boolean => s.enforced && s.state === 'reached'

// ── saying it ────────────────────────────────────────────────────────────────

/** "1 november" — the day the folds come back */
export function resetDay(s: Pick<FoldStanding, 'resetsAt'>): string {
  const d = new Date(s.resetsAt)
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(d).toLowerCase()
}

/** the meter, in words: "142 of 200 folds this month" · "1,204 folds this month" */
export function meterLine(s: FoldStanding): string {
  const n = s.used.toLocaleString('en-US')
  if (s.cap === null) return `${n} fold${s.used === 1 ? '' : 's'} this month, with no ceiling.`
  return `${n} of ${s.cap.toLocaleString('en-US')} folds this month. they come back on ${resetDay(s)}.`
}

/** what the plan tab and the dashboard say when the folds are spent */
export function reachedLine(s: FoldStanding): string {
  return `you have used this month's ${(s.cap ?? FREE_FOLD_CAP).toLocaleString('en-US')} folds, so netti has stopped folding. everything you made is still here, and every ai you connected still reads it. writing comes back on ${resetDay(s)}, or now with plus.`
}

/** the door's answer to a write past the folds: an ai relays it to the person word for word */
export function readOnlyWords(s: FoldStanding, planUrl: string): string {
  return `this innernet has used its ${(s.cap ?? FREE_FOLD_CAP).toLocaleString('en-US')} free folds for the month, so it is read-only until ${resetDay(s)}: nothing was written. everything already here still reads, in every ai. to keep writing now, the person can move to innernet plus (unlimited folds) at ${planUrl}.`
}

/** a 0..1 fill for the meter's line; an unlimited plan reads as a quiet quarter, never full */
export function meterFill(s: FoldStanding): number {
  if (s.cap === null) return 0.25
  return Math.min(1, s.used / s.cap)
}
